import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart, FaMapMarkerAlt } from "react-icons/fa";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";

export default function TopRatedHotels({ limit }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);
    const navigate = useNavigate();

    // Load wishlist from localStorage
    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlist(storedWishlist);
    }, []);

    // Save wishlist
    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    // Toggle wishlist
    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(item => item.id === hotel.id);

        let updated;
        if (exists) {
            updated = wishlist.filter(item => item.id !== hotel.id);
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(item => item.id === id);
    };

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get("http://127.0.0.1:8000/api/top-rated-hotels");
                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // ⭐ render stars
    const renderStars = (rating) => {
        const stars = [];
        const safeRating = rating || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(<FaStar key={i} />);
            } else if (i === Math.floor(safeRating) && safeRating % 1 >= 0.5) {
                stars.push(<FaStarHalfAlt key={i} />);
            } else {
                stars.push(<FaRegStar key={i} className="text-gray-300" />);
            }
        }

        return stars;
    };

    return (
        <section className="bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">

                    <div>
                        <span className="inline-flex items-center px-4 py-2 rounded-full bg-orange-50 text-[#FF6B35] text-sm font-semibold mb-4">
                            Top Rated Hotels
                        </span>

                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                            Luxury Stays Recommended For You
                        </h2>

                        <p className="text-slate-500 mt-3 max-w-2xl">
                            Handpicked hotels with exceptional ratings, premium amenities and
                            unbeatable locations across Dubai.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate("/hotels")}
                        className="hidden sm:flex items-center gap-2 px-6 py-3 bg-[#FF6B35] text-white rounded-xl hover:bg-orange-600 transition"
                    >
                        View All Hotels →
                    </button>

                </div>

                {/* Loading */}
                {loading ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin h-10 w-10 border-4 border-teal-500 border-t-transparent rounded-full"></div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
                            {hotels.slice(0, limit).map((hotel) => (
                                <div
                                    key={hotel.id}
                                    className="group bg-white rounded-[28px] overflow-hidden border border-slate-100 hover:shadow-2xl transition-all duration-500"
                                >
                                    {/* Image */}
                                    <div
                                        className="relative h-[280px] overflow-hidden cursor-pointer"
                                        onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                    >
                                        <img
                                            src={`http://127.0.0.1:8000/uploads/hotels/${hotel.image}`}
                                            alt={hotel.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />

                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent"></div>

                                        {/* Wishlist Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleWishlist(hotel);
                                            }}
                                            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition"
                                        >
                                            {isWishlisted(hotel.id) ? (
                                                <FaHeart className="text-red-500 text-sm sm:text-base" />
                                            ) : (
                                                <FaRegHeart className="text-gray-600 text-sm sm:text-base" />
                                            )}
                                        </button>

                                        {/* Rating Badge */}
                                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md rounded-full px-3 py-1 flex items-center gap-1 text-yellow-500 shadow-lg">
                                            {renderStars(hotel.rating)}
                                            <span className="ml-1 text-gray-700 text-[10px] sm:text-xs font-medium">
                                                ({hotel.rating})
                                            </span>
                                        </div>

                                        {/* Price */}
                                        <div className="absolute bottom-4 right-4 bg-[#FF6B35] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                                            {Math.round(hotel.price_per_night)} AED / night
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6">

                                        <h3
                                            onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                            className="text-xl font-bold text-slate-900 hover:text-[#FF6B35] transition cursor-pointer line-clamp-1"
                                        >
                                            {hotel.name}
                                        </h3>

                                        <div className="flex items-center gap-2 mt-2 text-slate-500">
                                            <FaMapMarkerAlt className="text-[#FF6B35]" />
                                            <span className="text-sm line-clamp-1">
                                                {hotel.location}
                                            </span>
                                        </div>

                                        <p className="text-slate-600 text-sm leading-6 mt-4 line-clamp-2">
                                            {hotel.short_description?.replace(/<[^>]*>/g, "")}
                                        </p>

                                        <button
                                            onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                            className="mt-6 w-full bg-[#FF6B35] text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition"
                                        >
                                            View Hotel
                                        </button>

                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Mobile Button */}
                        <div className="mt-10 sm:hidden">
                            <button
                                onClick={() => navigate("/hotels")}
                                className="w-full bg-[#FF6B35] text-white py-3 rounded-xl"
                            >
                                View All Hotels
                            </button>
                        </div>
                    </>
                )
                }
            </div >

        </section >
    );
}